// SessionProvider — single Context that owns:
//   - the WSClient singleton
//   - the SessionView reducer (RoomState + chat log + system messages)
//   - the DispatcherClient (HTTP) and AdminTokenStore / ResumeTokenStore (localStorage)
//   - high-level commands (joinRoom / sendChat / leaveRoom)
//   - Phase 5: snapshot apply on welcome + automatic reconnect with
//     exponential backoff + room.resume / room.resume-ok handling
//
// (architecture.md §16 — 共通 concern (WS 接続 / 描画エンジン / ストア /
//  色トークン) は Provider・hooks でレイアウト外に置く)
//
// Phase 4 Task 5b changes:
// - dispatcher 経由フローに移行: `join({ wsUrl, roomId, displayName })` で
//   呼び出し側 (App / CreateRoomPage / ReJoinScreen) が **解決済の wsUrl**
//   を渡す形に変更. env (`VITE_WS_URL`) 経由で wsUrl を持つ実装は廃止.
// - DispatcherClient / AdminTokenStore を context value で公開
//   (CreateRoomPage / ReJoinScreen から hook 経由で利用).
//
// Phase 6 Task 6 changes:
// - CreateRoomScreen が HomePage + CreateRoomPage に分割された.
//   SessionProvider 自身の責務 (transport / dispatcher / stores 公開) は不変.
//
// Phase 7 Task 7-10b changes:
// - `useFilteredUids` hook を組み込み、`filteredUids` (= ReadonlySet<string>) と
//   `toggleFilter(uid)` を context value で expose.
// - `useEffect` で `engine.setStrokeFilter(filteredUids)` を呼び、drawing
//   engine 側 (future-only filter) と web ローカル chat filter (render 段)
//   が同じ source-of-truth を共有する形にした.
// - chat 側は SessionProvider が filtering せず、ChatPanel の `filterChatByUids`
//   が render 時に絞る (= フィルタ解除で過去ログ復活、drawing と非対称).
//   詳細は architecture.md §19.14.

// Phase 5 Task 7 changes:
// - `room.welcome.snapshot` を受信したら `SnapshotLoader.loadSnapshot` を
//   走らせ、デコード済 RGBA を `engine.applySnapshot` に流す. snapshot の
//   後ろに `welcome.deltaEvents` (snapshot 以降のメモリ内イベント) を
//   `engine.applyRemoteStroke` で再生.
// - `welcome.resumeToken` を `ResumeTokenStore` に保存.
// - WS 切断 (close code != 1000) を検出したら exponential backoff で
//   ディスパッチャ再問い合わせ → WS 再接続 → `room.resume` 送信.
// - `room.resume-ok` を delta / snapshot 両モード対応で処理 (delta は
//   events 再生のみ、snapshot は loader → engine.applySnapshot →
//   deltaEvents 再生).
// - `room.rejected: 'resume-expired' | 'invalid-resume'` で resumeToken を
//   clear してリロード (UI 簡素 — Phase 6 で UX 改善).

import {
  type CustomCursor,
  DEFAULT_SNAPSHOT_BACKGROUND,
  RESUME_TOKEN_TTL_MS,
  type RoomWelcome,
  type ServerEvent,
  type StrokeCancelRejected,
  type WhiteboardRegionCancelRejected,
} from '@oekaki/protocol';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { DroppedSendTracker, flushDroppedSends } from '../../application/droppedSendDiagnostics.js';
import {
  type RemoteStrokeEngine,
  replayDeltaEventsToEngine,
  routeBroadcastToEngine,
} from '../../application/EngineBroadcastRouter.js';
import type { Transport } from '../../application/ports/Transport.js';
import {
  applyServerEvent,
  clearChatLog as clearChatLogReducer,
  INITIAL_SESSION_VIEW,
  markIdle,
  markJoining,
  markLeft,
  markReconnecting,
  type SessionView,
} from '../../application/RoomSession.js';
import { readReferrerFromCurrentUrl } from '../../application/referrerFromUrl.js';
import { shouldDispatchChatMessage } from '../../application/uiFeedback.js';
import {
  changeCursor as changeCursorCmd,
  changeName as changeNameCmd,
  draftCursor as draftCursorCmd,
  joinRoom as joinRoomCmd,
  leaveRoom as leaveRoomCmd,
  type SendImagepasteRequestArgs,
  sendBackgroundSet as sendBackgroundSetCmd,
  requestCancelRegion as sendCancelRegionCmd,
  requestCancelStroke as sendCancelStrokeCmd,
  sendChat as sendChatCmd,
  sendClear as sendClearCmd,
  sendImagepasteConfirm as sendImagepasteConfirmCmd,
  sendImagepasteRequest as sendImagepasteRequestCmd,
  sendKick as sendKickCmd,
  setRom as setRomCmd,
} from '../../application/useCases.js';
import { getClientBuild } from '../../infrastructure/clientBuild.js';
import {
  createDispatcherClient,
  type DispatcherClient,
} from '../../infrastructure/dispatcher/DispatcherClient.js';
import { createDotByDotPreferenceStore } from '../../infrastructure/dotByDotPreference.js';
import type { DotByDotMode } from '../../infrastructure/drawing/zoom.js';
import { readEnv } from '../../infrastructure/env.js';
import {
  createLayoutPreferenceStore,
  type LayoutPreferenceStore,
} from '../../infrastructure/layoutPreference.js';
import { createConsoleLogger } from '../../infrastructure/logging/ConsoleLogger.js';
import { captureWarning } from '../../infrastructure/sentryCapture.js';
import {
  createReplayProbe,
  type JoinReplaySource,
  reportSnapshotLoadFailure,
  reportSnapshotLoadThrew,
} from '../../infrastructure/snapshot/joinReplayDiagnostics.js';
import {
  type DecodedSnapshotLayer,
  loadSnapshot,
  type SnapshotLoadOutcome,
} from '../../infrastructure/snapshot/SnapshotLoader.js';
import {
  reportSnapshotDiagnostics,
  type SnapshotLoadSource,
} from '../../infrastructure/snapshot/snapshotDiagnostics.js';
import {
  type AdminTokenStore,
  createAdminTokenStore,
} from '../../infrastructure/storage/AdminTokenStore.js';
import {
  type AnonUidStore,
  createAnonUidStore,
} from '../../infrastructure/storage/AnonUidStore.js';
import {
  type CustomCursorStore,
  createCustomCursorStore,
} from '../../infrastructure/storage/CustomCursorStore.js';
import {
  createDisplayNameStore,
  type DisplayNameStore,
} from '../../infrastructure/storage/DisplayNameStore.js';
import {
  createResumeTokenStore,
  type ResumeTokenStore,
} from '../../infrastructure/storage/ResumeTokenStore.js';
import { WSClient } from '../../infrastructure/transport/WSClient.js';
import { useFilteredUids } from '../hooks/useFilteredUids.js';
import { resolveJoinTelemetry } from '../layouts/layoutResolution.js';
import { readPhoneViewportInput } from '../layouts/useLayoutPreference.js';
import { ConnectionLockContext, type ConnectionLockValue } from './ConnectionLockContext.js';

// ─── Reconnect / snapshot constants ──────────────────────────────────────────

// `RESUME_TOKEN_TTL_MS` は protocol package から re-import している (2026-05
// 役割分離). サーバ側 TTL が source of truth (`@oekaki/protocol`); クライアント
// 側は localStorage の保持期間 = 「sleep 復帰時に doomed `room.resume` を撃た
// ない」optimistic upper bound として参照する.

/** room.join に載せる画面の拡大率 (0 / 非有限なら載せない)。canvas-dot-by-dot-2026-10-28.md */
const dprField = (): { dpr?: number } => {
  const v = typeof window !== 'undefined' ? window.devicePixelRatio : undefined;
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? { dpr: v } : {};
};

/** room.join に載せるドットバイドット表示の設定 (未保存なら既定 'auto')。 */
const dotByDotField = (): { dotByDot: DotByDotMode } => ({
  dotByDot: createDotByDotPreferenceStore().get(),
});

/**
 * Backoff schedule (ms): 1/2/4/8s の指数で立ち上がり、以降 15s 間隔で打ち続ける。
 * 合計 4 分 15 秒・20 回。
 *
 * 2026-09-30 作者 fb: 旧 5 回 (1/2/4/8/16s、~31s) では本番デプロイ (wss 停止 →
 * 再作成 → dispatcher healthy → wss 起動) の停止時間に間に合わず、デプロイの度に
 * 在室者が ErrorScreen に落ちていた。上限は resume token TTL (`RESUME_TOKEN_TTL_MS`
 * = 5 分) の内側に収める (= 最後の試行でも room.resume が通る)。15s 間隔は「復帰を
 * 待たせすぎない」と「停止中の dispatcher を叩きすぎない」の折衷。非表示タブは
 * 試行を消費しない (ws-heartbeat-2026-09-29.md §2-3) ので、長くしても裏タブの
 * 無駄打ちは増えない。
 */
export const RECONNECT_BACKOFFS_MS: readonly number[] = [
  1_000,
  2_000,
  4_000,
  8_000,
  ...Array.from({ length: 16 }, () => 15_000),
];

const MAX_RECONNECT_ATTEMPTS = RECONNECT_BACKOFFS_MS.length;

/** snapshot 適用待ちの描画ロックを強制解除するまでの上限 (保険)。 */
const HYDRATING_SAFETY_MS = 15_000;

// ─── Reducer plumbing ────────────────────────────────────────────────────────

type Action =
  | { type: 'server-event'; event: ServerEvent }
  | { type: 'mark-joining'; roomId: string; displayName: string }
  | { type: 'mark-left' }
  | { type: 'mark-reconnecting'; attempt: number; nextAttemptAt: number }
  | { type: 'system-line'; text: string }
  /** Phase 7 Task 7-9: `/clear` slash command. */
  | { type: 'clear-chat-log' }
  /**
   * 入室あいことば不一致 (`room.rejected { reason: 'invalid-entry-password' }`)
   * — `joining` から `idle` に巻き戻して `ReJoinScreen` へ再度落とす.
   */
  | { type: 'mark-idle' };

const reducer = (view: SessionView, action: Action): SessionView => {
  switch (action.type) {
    case 'server-event':
      return applyServerEvent(view, action.event);
    case 'mark-joining':
      return markJoining(view, action.roomId, action.displayName);
    case 'mark-left':
      return markLeft(view);
    case 'mark-reconnecting':
      return markReconnecting(view, action.attempt, action.nextAttemptAt);
    case 'system-line':
      return {
        ...view,
        system: [...view.system, { ts: Date.now(), text: action.text }],
      };
    case 'clear-chat-log':
      return clearChatLogReducer(view);
    case 'mark-idle':
      return markIdle(view);
  }
};

// ─── Public surface ──────────────────────────────────────────────────────────

export interface JoinArgs {
  /** Resolved by the dispatcher (`createRoom` or `lookupRoomServer`). */
  readonly wsUrl: string;
  readonly roomId: string;
  readonly displayName: string;
  readonly isRom?: boolean;
  /**
   * 入室「あいことば」(owner-vs-entry-password-2026-06-13.md). あいことば付き
   * 部屋の visitor 入室でのみ渡す — `ReJoinScreen` が
   * `lookup.roomMeta.entryPasswordRequired` を見て入力欄を出すかどうかを
   * 決める。admin_token を持つ入室 (= オーナー) では呼び出し側が渡さない
   * (admin mode では黙って無視される、`useCases.joinRoom` 参照)。
   */
  readonly entryPassword?: string;
}

/**
 * 入室あいことば不一致で入室画面に巻き戻すときに引き継ぐ最小限の情報.
 * `SessionContextValue.entryPasswordRejection` として公開する.
 */
export interface EntryPasswordRejection {
  readonly roomId: string;
  /** 巻き戻し後の `ReJoinScreen` が表示名入力欄を再入力なしで維持するための値. */
  readonly displayName: string;
}

export interface SessionContextValue {
  readonly view: SessionView;
  readonly transport: Transport;
  /** dispatcher HTTP client (for entry flow / Phase 6 admin API). */
  readonly dispatcher: DispatcherClient;
  /** admin token persistence (localStorage with in-memory fallback). */
  readonly adminTokens: AdminTokenStore;
  /** resume token persistence (localStorage with in-memory fallback). */
  readonly resumeTokens: ResumeTokenStore;
  /**
   * 入室画面の名前記憶 (端末に 1 つ). welcome 受信時に Provider が保存し、
   * ReJoinScreen が初期値として読む. 省略 (手組みの test context 等) 可 —
   * その場合 ReJoinScreen は自前で既定 store を作る.
   */
  readonly displayNames?: DisplayNameStore;
  /**
   * Connect to the WS server (using the dispatcher-resolved `wsUrl`) and
   * send `room.join`. Returns immediately; status is reflected in `view.phase`.
   */
  readonly join: (args: JoinArgs) => void;
  /**
   * 入室あいことば不一致 (`room.rejected { reason: 'invalid-entry-password' }`)
   * で入室画面へ巻き戻されたときの引き継ぎ情報. `null` = 該当なし.
   * `ReJoinScreen` が mount 時にこれを読み、roomId が一致すれば表示名を
   * 引き継いで「あいことばが違います」をあいことば欄の下に表示し、
   * `clearEntryPasswordRejection()` で消費済にする.
   */
  readonly entryPasswordRejection: EntryPasswordRejection | null;
  /** `entryPasswordRejection` を消費済にする (`ReJoinScreen` が読み取り後に呼ぶ). */
  readonly clearEntryPasswordRejection: () => void;
  /** Send `chat.send`. */
  readonly sendChat: (text: string) => void;
  /** Send `member.rom`. */
  readonly setRom: (rom: boolean) => void;
  /**
   * Send `member.name-change` (Phase 7 Task 7-2). Server validates length /
   * applies a 5-second per-connection throttle and silently drops invalid /
   * duplicate / throttled requests (no error event), so callers may fire
   * this freely. Successful changes arrive back via `member.name-changed`.
   *
   * 入力 UI (slash command `/name`, settings modal) は Task 7-9 で組み込む.
   */
  readonly sendNameChange: (displayName: string) => void;
  /**
   * Send `member.cursor-change` (custom-cursor-2026-09-19.md §2.3) — the
   * "決定" / "カーソルを外す" actions in the cursor editor. Also persists (or
   * clears, for `cursor: null`) the value in `CustomCursorStore` so it's
   * restored on the next fresh join. Server throttles to once per 2s and
   * silently drops excess/invalid requests; success is observable via the
   * `member.cursor-changed` broadcast (reflected into `RoomState.members[]`).
   */
  readonly sendCursorChange: (cursor: CustomCursor | null) => void;
  /**
   * Send `member.cursor-draft` (custom-cursor-2026-09-19.md §2.3) — the
   * cursor editor's live in-progress state while open. `cursor: null` means
   * editing ended/cancelled. Callers (the editor) are responsible for
   * throttling to ≤5/s and only calling this when the pixels actually
   * changed (§2.4) — this is a thin, unthrottled wire pass-through.
   */
  readonly sendCursorDraft: (cursor: CustomCursor | null) => void;
  /**
   * Send `admin.kick` (Phase 7 Task 7-4). Server validates `Perm.KICK` on
   * the caller. Rejections arrive as `admin.kick-rejected`; success is
   * observable via `room.member-left { reason: 'kick', kickerUid }` (which
   * the reducer renders into the chat panel system log).
   *
   * 入力 UI (`/kick` slash command, admin panel) は Task 7-9 で組み込む.
   */
  readonly sendKick: (args: { targetUid: string; reason?: string }) => void;
  /**
   * Send `whiteboard.clear` (Phase 7 Task 7-5). Server gates on
   * `room.clearOpen || hasPerm(Perm.CLEAR)` and replies with `system.error
   * { code: 'no-permission' }` on failure. Success is observable via the
   * `whiteboard.clear` broadcast (the reducer renders a chat system line +
   * the drawing engine wipes the canvas via `routeBroadcastToEngine`).
   *
   * 入力 UI (`/clear` slash command) は Task 7-9 で組み込む.
   *
   * W3 Classic レイヤー追加 (consult 2026-05-26): 引数で clear scope を選ぶ.
   *   - `sendClear()` (= layerId 省略) → 全層 clear + snapshot session rotate
   *     (= 従来の「全消し」). ConfirmDialog 経由.
   *   - `sendClear(layerId)` (= Classic 0/1/2) → その層のみ clear / session 継続.
   *     LayerPopup の「その層を clear」経路. broadcast に layerId が乗る.
   */
  readonly sendClear: (layerId?: number) => void;
  /**
   * Send `stroke.cancel` (conflict-aware Undo, β Phase 10,
   * docs/_wip/undo-conflict-aware-2026-07-12.md / protocol-events.md §1.1).
   * Thin command wrapper — mirrors `sendClear`. `sid` should come from
   * `engine.getCancellableStrokes()` (the engine holds the LIFO stack; the
   * UI presentation of it, and of `stroke.cancel-rejected` results surfaced
   * via `engine.onCancelRejected`, is a later task — this seam only wires
   * the send/receive plumbing).
   */
  readonly sendCancelStroke: (sid: string) => void;
  /**
   * Send `whiteboard.region-cancel` (範囲の移動・コピーの「元に戻す」)。`regionSeq` は
   * `engine.getCancellableOps()` の `kind: 'region'` の値。拒否は `stroke.cancel-rejected` と同じ
   * `engine.onCancelRejected` に流れる。
   */
  readonly sendCancelRegion: (regionSeq: number) => void;
  /**
   * Send `whiteboard.background-set` (owner canvas background color, A-2
   * transport; docs/_wip/canvas-background-color-2026-06-20.md §4.3). Mirrors
   * `sendClear`: server gates on `hasPerm(Perm.CLEAR)` alone (decoupled from
   * `room.clearOpen`) and replies `system.error { code: 'no-permission' }` on
   * failure. Success is observable via the `whiteboard.background-set`
   * broadcast — the originator too applies it on receipt (no optimistic local
   * apply), wired into `engine.setBackground({ rgb, alpha: 255 })`.
   *
   * `rgb` is a 24-bit RGB int; alpha is fixed opaque server-side. The A-4 ■
   * swatch UI (`useRoomChrome.handleBackgroundColorChange`) calls this through
   * a trailing debounce so a wheel drag commits a single broadcast.
   */
  readonly sendBackgroundSet: (rgb: number) => void;
  /**
   * Subscribe to the room's current canvas background colour (rgb24, A-4 ■
   * swatch; docs/_wip/canvas-background-color-2026-06-20.md §4.2). The engine
   * has no `getBackground()` getter, so React (= `useRoomChrome`) keeps the
   * authoritative bg-colour state; this seam pushes it the *remote* sources of
   * truth so a non-owner ■ reflects another client's change:
   *   - the `whiteboard.background-set` broadcast (the same one routed into
   *     `engine.setBackground`), and
   *   - a snapshot's `meta.background` on (re)join (initial seed).
   * The callback fires after the engine has already been mutated. Returns an
   * unsubscribe fn. Listeners are notified for every change including the
   * caller's own broadcast echo (idempotent — `useRoomChrome` collapses no-ops).
   */
  readonly onBackgroundChange: (cb: (rgb: number) => void) => () => void;
  /**
   * Phase 7 Task 7-7: stage 1 of the imagepaste 3-stage flow. Sends
   * `whiteboard.imagepaste-request`; server replies with either
   * `whiteboard.imagepaste-token { uploadId, putUrl, expiresAt }` or
   * `whiteboard.imagepaste-rejected { reason }`. The Provider does NOT
   * automatically perform the presigned PUT — that's Task 7-8 (UI + drawing
   * engine integration). Callers observe the responses by hooking the
   * transport directly (= `useImagepaste`). 2026-06-14: rejection は
   * `view.system` ではなく `useImagepaste` の `rejectToast` (キャンバス中央
   * トースト) で surface するようになった。
   *
   * `width`/`height` are required (server can't recover them from S3).
   * Callers MUST read them via the File API before invoking this.
   */
  readonly sendImagepasteRequest: (args: SendImagepasteRequestArgs) => void;
  /**
   * Phase 7 Task 7-7: stage 2 of the imagepaste flow. Call this only after
   * a successful presigned PUT. Server HEADs the object and either
   * broadcasts `whiteboard.imagepaste` (success) or replies with
   * `whiteboard.imagepaste-rejected` (`upload-failed` / `invalid-upload`).
   */
  readonly sendImagepasteConfirm: (uploadId: string) => void;
  /** Send `room.leave` semantics (close socket). */
  readonly leave: () => void;
  /**
   * Register the local drawing engine so the Provider can replay incoming
   * stroke / whiteboard broadcasts onto it. Pass `null` to unregister
   * (e.g. on unmount). architecture.md §19.6 — Canvas は frontend が配置、
   * 中身は drawing-engine が操作する接続点.
   */
  readonly registerEngine: (engine: RemoteStrokeEngineWithSnapshot | null) => void;
  /**
   * Phase 7 Task 7-8b: append a free-form line to `view.system` so UI
   * surfaces (chat panel) can show it.
   *
   * 2026-06-14: imagepaste 関連 (client-side PUT 失敗 / createImageBitmap 失敗 /
   * server rejection) は全て `useImagepaste` の `rejectToast` (キャンバス中央
   * トースト) に移設したため、imagepaste 経路ではもう呼ばれない。残る利用者は
   * `/help` `/clear` 等のスラッシュコマンドやその他の system 通知。
   */
  readonly pushSystemLine: (text: string) => void;
  /**
   * Phase 7 Task 7-9: `/clear` slash command の実体. ローカルでチャット欄
   * (chat + system) を空にする. **キャンバスのクリアではない** ことに注意:
   * canvas wipe は `sendClear()` (`whiteboard.clear` を server に投げる)
   * 経路で、こちらは ToolPanel「全消し」ボタンに集約済.
   *
   * `maintenance` banner state は維持される (system 行に履歴があったとしても、
   * banner の `scheduled` / `countdown` は別 state).
   */
  readonly clearChatLog: () => void;
  /**
   * Phase 7 Task 7-10b: client-local uid フィルタの現在値.
   * drawing engine の future-only filter (engine.setStrokeFilter) と
   * ChatPanel の render-time filter (filterChatByUids) の両方で参照.
   * 自分の uid は絶対に含まれない (preview commit hang 防止、
   * `useFilteredUids` 内で defensive guard).
   */
  readonly filteredUids: ReadonlySet<string>;
  /**
   * Phase 7 Task 7-10b: フィルタの ON/OFF 反転. self-uid を渡しても no-op
   * (defensive). MemberPanel の各行のトグルボタンから呼ばれる.
   */
  readonly toggleFilter: (uid: string) => void;
}

/**
 * Engine surface needed by the Provider. Extends `RemoteStrokeEngine`
 * (broadcast replay) with `applySnapshot` for the Phase 5 path and
 * `setStrokeFilter` for Phase 7 Task 7-10b. Concrete `CanvasDrawingEngine`
 * satisfies it; tests substitute spies.
 */
export interface RemoteStrokeEngineWithSnapshot extends RemoteStrokeEngine {
  /**
   * Apply decoded snapshot layers. `opts.resetAll` (past-canvas ライブ復元
   * hotfix, 2026-07-12) clears every layer + drops in-flight strokes before
   * blitting the seed, atomically — the restore path uses it so the pixel
   * wipe rides the same immediate channel as the seed apply (no delayed
   * `applyRemoteStroke([clear])` that races the seed and blanks the canvas).
   */
  applySnapshot(
    layers: ReadonlyArray<{ id: number; rgba: Uint8ClampedArray }>,
    opts?: { resetAll?: boolean },
  ): void;
  /**
   * Drop every peer NameLabel (main-thread display state). The restore path
   * calls this for the non-pixel cleanup that `applyRemoteStroke([clear])`
   * used to bundle with the (now folded-into-`applySnapshot`) pixel wipe.
   */
  clearNameLabels(): void;
  /**
   * Replace the canvas background. Called when a snapshot's `meta.json`
   * carries a non-default `background` so the engine composites the
   * historical bg under the snapshot pixels (= late joiners see the
   * exact same canvas the originating client saw).
   * (docs/_wip/layer-background-separation-2026-05-17.md)
   */
  setBackground(next: { rgb: number; alpha: number }): void;
  /**
   * Phase 7 Task 7-10b: future-only stroke / imagepaste filter by uid.
   * 過去に Layer.main へ焼かれた絵には影響しない (architecture.md §19.14).
   * Provider が `filteredUids` の変化を `useEffect` で観測してこれを呼ぶ.
   */
  setStrokeFilter(uids: ReadonlySet<string>): void;
  /**
   * conflict-aware Undo (C4) — feed a single `ServerEvent` into the B'
   * baseline mechanism. NOT gated by self-uid echo suppression (unlike
   * `routeBroadcastToEngine`) — see `CanvasDrawingEngine.ingestUndoBroadcast`.
   */
  ingestUndoBroadcast(event: unknown): void;
  /** Batch counterpart for `room.welcome.deltaEvents` / `room.resume-ok.events`. */
  ingestUndoBroadcastBatch(events: ReadonlyArray<unknown>): void;
  /**
   * Seed the B' baseline from an already-decoded snapshot (mirrors
   * `applySnapshot`'s own `resetAll`). Call alongside `applySnapshot` with
   * the same `layers`.
   */
  seedUndoBaseline(
    layers: ReadonlyArray<{ id: number; rgba: Uint8ClampedArray }>,
    watermarkSeq: number,
    resetAll?: boolean,
  ): void;
  /** Feed a `stroke.cancel-rejected` / `whiteboard.region-cancel-rejected` unicast payload. */
  notifyCancelRejected(payload: StrokeCancelRejected | WhiteboardRegionCancelRejected): void;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export interface SessionProviderProps {
  /** Override transport for tests / Storybook. */
  readonly transport?: Transport;
  /** Override DispatcherClient for tests / Storybook. */
  readonly dispatcher?: DispatcherClient;
  /** Override AdminTokenStore for tests / Storybook. */
  readonly adminTokens?: AdminTokenStore;
  /**
   * Override AnonUidStore for tests / Storybook (Phase 8 Task 8-5).
   * Default: `createAnonUidStore()` which auto-probes `globalThis.localStorage`
   * + `globalThis.crypto.randomUUID`.
   */
  readonly anonUids?: AnonUidStore;
  /** Override ResumeTokenStore for tests / Storybook. */
  readonly resumeTokens?: ResumeTokenStore;
  /**
   * Override CustomCursorStore for tests / Storybook
   * (custom-cursor-2026-09-19.md §2.3). Default: `createCustomCursorStore()`
   * which auto-probes `globalThis.localStorage`.
   */
  readonly customCursors?: CustomCursorStore;
  /** Override DisplayNameStore for tests. */
  readonly displayNames?: DisplayNameStore;
  /**
   * Override the snapshot loader (tests only). Defaults to the real
   * `SnapshotLoader.loadSnapshot`. The Provider passes both `metaUrl`
   * and the fresh-signed `layerUrls` it received on
   * `room.welcome.snapshot` / `room.resume-ok.snapshot` (the layer URLs
   * are not embedded in meta.json — see `SnapshotLoader.ts` header for
   * the TTL bug rationale).
   */
  readonly loadSnapshotFn?: (
    metaUrl: string,
    layerUrls: ReadonlyArray<{ id: number; url: string }>,
    source?: SnapshotLoadSource,
  ) => Promise<SnapshotLoadOutcome>;
  /**
   * Override the referrer (`?ref=...`) for tests / Storybook (Phase 8
   * Task 8-5). Default: read from `window.location.search` once at mount via
   * `readReferrerFromCurrentUrl()`. Pass `null` to suppress.
   */
  readonly initialReferrer?: string | null;
  /**
   * Override LayoutPreferenceStore for tests / Storybook
   * (pen-usage-analytics-2026-08-18.md §9). Default:
   * `createLayoutPreferenceStore()` which auto-probes `globalThis.localStorage`.
   * Read at `room.join` construction time so the value matches what the
   * layout hook (`useLayoutPreference`) is about to render.
   */
  readonly layoutPreference?: LayoutPreferenceStore;
  readonly children: ReactNode;
}

export const SessionProvider = ({
  transport,
  dispatcher,
  adminTokens,
  anonUids,
  resumeTokens,
  customCursors,
  displayNames,
  loadSnapshotFn,
  initialReferrer,
  layoutPreference,
  children,
}: SessionProviderProps): ReactNode => {
  const [view, dispatch] = useReducer(reducer, INITIAL_SESSION_VIEW);
  // 入室あいことば不一致の巻き戻し引き継ぎ (React state — ReJoinScreen の
  // remount を跨いで読めるよう Context 経由で公開する必要があるため ref ではなく
  // state。`entryPasswordRejection` 参照 doc コメントも参照).
  const [entryPasswordRejection, setEntryPasswordRejection] =
    useState<EntryPasswordRejection | null>(null);
  const clearEntryPasswordRejection = useCallback((): void => {
    setEntryPasswordRejection(null);
  }, []);

  // Keep one WSClient for the lifetime of the page session.
  const transportRef = useRef<Transport | null>(transport ?? null);
  if (transportRef.current === null) {
    transportRef.current = new WSClient({ logger: createConsoleLogger({ level: 'debug' }) });
  }
  const tx = transportRef.current;

  // DispatcherClient and stores are also singletons per page.
  const dispatcherRef = useRef<DispatcherClient | null>(dispatcher ?? null);
  if (dispatcherRef.current === null) {
    const { dispatcherUrl } = readEnv();
    dispatcherRef.current = createDispatcherClient({
      baseUrl: dispatcherUrl,
      logger: createConsoleLogger({ level: 'debug' }),
    });
  }
  const dispatcherClient = dispatcherRef.current;

  const adminTokenStoreRef = useRef<AdminTokenStore | null>(adminTokens ?? null);
  if (adminTokenStoreRef.current === null) {
    adminTokenStoreRef.current = createAdminTokenStore();
  }
  const adminTokenStore = adminTokenStoreRef.current;

  // Phase 8 Task 8-5: anonymous user identifier persistence (analytics fingerprint).
  // Singleton per page session; the underlying store auto-probes localStorage
  // + crypto.randomUUID and silently degrades to "always returns null"
  // (= clientAnonId omitted from room.join) if either is unavailable.
  const anonUidStoreRef = useRef<AnonUidStore | null>(anonUids ?? null);
  if (anonUidStoreRef.current === null) {
    anonUidStoreRef.current = createAnonUidStore();
  }
  const anonUidStore = anonUidStoreRef.current;

  // pen-usage-analytics-2026-08-18.md §9: layout preference store singleton.
  // 読み出しは `room.join` を組み立てるまさにその箇所 (2 join call site) だけで
  // 行う — このハンドルは値を保持せず store への参照だけ持つ (= 常に最新の
  // localStorage 値を `.get()` で読む、useLayoutPreference の React state とは
  // 別経路だが同じ store 実装/キーを共有する).
  const layoutPreferenceStoreRef = useRef<LayoutPreferenceStore | null>(layoutPreference ?? null);
  if (layoutPreferenceStoreRef.current === null) {
    layoutPreferenceStoreRef.current = createLayoutPreferenceStore();
  }
  const layoutPreferenceStore = layoutPreferenceStoreRef.current;

  // Phase 8 Task 8-5: snapshot the `?ref=...` query at mount.  Using a ref
  // (not state) because referrer never changes during the page lifetime —
  // reading once and persisting through URL navigations (HomePage push/replace
  // dance, ActionCreateSplash) means the eventual `room.join` still carries
  // the original landing referrer even after the URL has been rewritten to
  // pathname `/<uuid>`. Cleared on `leave()` to avoid double-attribution if
  // the user re-enters via in-app navigation.
  const referrerRef = useRef<string | null>(
    initialReferrer !== undefined ? initialReferrer : readReferrerFromCurrentUrl(),
  );

  const resumeTokenStoreRef = useRef<ResumeTokenStore | null>(resumeTokens ?? null);
  if (resumeTokenStoreRef.current === null) {
    resumeTokenStoreRef.current = createResumeTokenStore();
  }
  const resumeTokenStore = resumeTokenStoreRef.current;

  // custom-cursor-2026-09-19.md §2.3: per-device saved cursor. Restored on a
  // fresh `room.welcome` (not on resume — the server already carries it
  // across a resume, see the `room.welcome` handler below).
  const customCursorStoreRef = useRef<CustomCursorStore | null>(customCursors ?? null);
  if (customCursorStoreRef.current === null) {
    customCursorStoreRef.current = createCustomCursorStore();
  }
  const customCursorStore = customCursorStoreRef.current;

  // 入室画面の名前記憶: fresh `room.welcome` (= 実際に入れた時点) で保存する.
  const displayNameStoreRef = useRef<DisplayNameStore | null>(displayNames ?? null);
  if (displayNameStoreRef.current === null) {
    displayNameStoreRef.current = createDisplayNameStore();
  }
  const displayNameStore = displayNameStoreRef.current;

  // Snapshot loader (tests can inject).
  const snapshotLoader = useMemo(
    () =>
      loadSnapshotFn ??
      ((
        metaUrl: string,
        layerUrls: ReadonlyArray<{ id: number; url: string }>,
        source: SnapshotLoadSource = 'welcome',
      ) =>
        loadSnapshot(
          { metaUrl, layerUrls },
          { onDiagnostics: (d) => reportSnapshotDiagnostics(d, source) },
        )),
    [loadSnapshotFn],
  );

  // Local drawing engine handle. Set by `<DrawingCanvas onEngineReady={...}>`
  // through `registerEngine`. The engine ref lives outside React state on
  // purpose: it changes once at mount and once at unmount, and we don't want
  // re-renders to re-fire the `transport.onEvent` subscription.
  const engineRef = useRef<RemoteStrokeEngineWithSnapshot | null>(null);
  // Self-uid (derived from view.phase). Both the broadcast listener (via
  // `selfUidRef`) and the filter hook (via `useFilteredUids({ selfUid })`)
  // need this. Compute once per render so the hook gets a stable string.
  const selfUid: string | null =
    view.phase.t === 'joined' || view.phase.t === 'reconnecting' ? view.phase.state.uid : null;
  // Self-uid mirror so the broadcast listener (a static closure created
  // once per `tx`) can suppress echoes without re-subscribing every time
  // the SessionView changes.
  const selfUidRef = useRef<string | null>(null);
  selfUidRef.current = selfUid;

  // ─── Phase 7 Task 7-10b — client-local stroke / chat filter ────────────────
  // The state Set lives here so both drawing engine (future-only filter via
  // setStrokeFilter) and ChatPanel (render-time filter via filterChatByUids)
  // share a single source of truth. The hook itself defends against putting
  // self-uid into the set (= preview commit hang).
  const { filteredUids, toggleFilter } = useFilteredUids({ selfUid });
  // Push the filter set into the drawing engine whenever it changes.
  // Re-runs also fire on engine (re)mount via `registerEngine` (we re-flush
  // there so the engine doesn't lose state across canvas remount). When the
  // engine is null (canvas unmounted) we just skip — the next registerEngine
  // call will sync.
  useEffect(() => {
    const engine = engineRef.current;
    if (engine === null) return;
    engine.setStrokeFilter(filteredUids);
  }, [filteredUids]);

  // Phase ref for the close-handler reconnect gate (avoid re-subscribing
  // the transport listener on every reducer transition).
  const phaseTagRef = useRef<SessionView['phase']['t']>(view.phase.t);
  phaseTagRef.current = view.phase.t;

  // 再接続中は描けない (docs/_wip/reconnect-draw-lock-2026-10-12.md): 単一の判定。
  // context で配る (描画入口 / ボタン / チャット) ほか、サーバへ送る関数側にも ref で
  // 同じ門を置く (UI 側の止め漏れがあっても `WSClient.send` の黙殺頼みにしない)。
  // snapshot 適用完了まで描画ロックを延ばす (welcome → joined で即解除すると、適用前に
  // 描き始めた線が `applySnapshot` (resetAll / レイヤ全置換) で消える)。帯は出さない。
  const [hydrating, setHydrating] = useState(false);
  useEffect(() => {
    if (!hydrating) return;
    // 取りこぼし防止の保険 (snapshot 取得が想定外に詰まっても描画が永久に塞がらない)。
    const timer = setTimeout(() => setHydrating(false), HYDRATING_SAFETY_MS);
    return () => clearTimeout(timer);
  }, [hydrating]);
  const connectionLocked = view.phase.t === 'reconnecting' || hydrating;
  const connectionLockedRef = useRef(connectionLocked);
  connectionLockedRef.current = connectionLocked;
  const reconnectAttempt = view.phase.t === 'reconnecting' ? view.phase.attempt : 0;
  const connectionLock = useMemo<ConnectionLockValue>(
    () => ({
      locked: connectionLocked,
      ...(hydrating ? { hydrating: true } : {}),
      attempt: reconnectAttempt,
      maxAttempts: RECONNECT_BACKOFFS_MS.length,
    }),
    [connectionLocked, hydrating, reconnectAttempt],
  );

  // ─── Phase 5: reconnect bookkeeping ────────────────────────────────────────
  /**
   * Last-known room context — used to drive `room.resume` after the WS
   * is back open. `null` while idle / pre-welcome / after explicit leave.
   */
  const sessionContextRef = useRef<{
    roomId: string;
    uid: string;
    displayName: string;
    isRom: boolean;
    wsUrl: string;
  } | null>(null);
  /**
   * Phase 7.5: 直近 `join()` の引数を保持. `room.rejected.reason ===
   * 'invalid-admin-token'` の自動 fallback (admin_token を捨てて visitor で
   * 再 join) と、resume failure → fresh visitor join 経路で displayName /
   * isRom を再利用するためのスナップショット.
   *
   * `leave()` で null にリセットする (= 明示退室後は fallback 対象外).
   */
  const lastJoinArgsRef = useRef<{
    roomId: string;
    displayName: string;
    isRom: boolean;
    wsUrl: string;
    /** 入室あいことば (メモリのみ。再起動後の自動 rejoin で再送する). */
    entryPassword?: string;
  } | null>(null);
  /**
   * true = 直近に `room.resume` を送り、その応答 (welcome / resume-ok / rejected)
   * をまだ受けていない. この間に `room-not-found` / `resume-expired` /
   * `invalid-resume` で拒否されたら (= wss 再起動でメモリ上の部屋・token が消えた)
   * ErrorScreen に落とさず fresh join し直す. 応答を受けた時点で必ず false に
   * 落とす (= 自動 rejoin は 1 回だけ: rejoin の `room.join` への拒否は
   * resume の文脈ではないので従来どおり ErrorScreen).
   */
  const resumeInFlightRef = useRef<boolean>(false);
  /**
   * true = 再接続中に fresh `room.join` を送った (resume token 無し / resume 拒否後の
   * fallback)。その welcome は mount 済みの RoomLayout / engine に流し込まれる
   * (phase を reconnecting のまま保つので remount しない) ため、旧 wss に居た間に
   * 描かれて新 wss 側に無い (未 snapshot の) ストロークがクライアントに残らないよう
   * `resetAll: true` で適用する。welcome で 1 回消費。通常の `join()` では false。
   */
  const freshRejoinRef = useRef<boolean>(false);
  /** Most recent `lastSeq` we acknowledged — sent in `room.resume`. */
  const lastSeqRef = useRef<number>(0);
  /** Pending reconnect timer (so we can cancel on unmount / explicit leave). */
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Reconnect attempt counter (1-based). Resets to 0 on successful welcome / resume-ok. */
  const reconnectAttemptRef = useRef<number>(0);
  /**
   * Set true while the user explicitly leaves so the close handler skips
   * the auto-reconnect schedule.
   */
  const intentionalCloseRef = useRef<boolean>(false);
  /**
   * ws-heartbeat-2026-09-29.md §2-3: set true when `scheduleReconnect` was
   * asked to run while `document.hidden` — backgrounded tabs (esp. mobile
   * Safari/Chrome) don't return WS PONGs, so the server's new PONG-miss
   * `terminate()` (§2-1) can close the socket repeatedly while hidden. We
   * don't want that to silently burn through `MAX_RECONNECT_ATTEMPTS` and
   * land the user on `ErrorScreen` the moment they switch back — instead we
   * defer (no timer, no attempt consumed) and let `resumeDeferredReconnect`
   * (visibilitychange → visible / online) reset the counter and restart.
   */
  const hiddenPendingReconnectRef = useRef<boolean>(false);
  // 診断のみ (挙動不変): socket 未 open で捨てられた描画イベントの件数。復帰 / pagehide で 1 件報告。
  const droppedSendTrackerRef = useRef<DroppedSendTracker>(new DroppedSendTracker());
  /**
   * Pending welcome snapshot — set when welcome arrives, cleared after
   * `engine.applySnapshot` runs. Decouples welcome timing (sync) from
   * snapshot fetch (async) and from engine registration timing (mounts
   * after ClassicLayout renders).
   */
  const pendingSnapshotRef = useRef<{
    layers: readonly DecodedSnapshotLayer[];
    deltaEvents: readonly unknown[];
    /**
     * Canvas background from meta.json (= snapshot capture-time bg).
     * Applied to the engine BEFORE `applySnapshot` so the layer pixels
     * (which are α-aware since 2026-05-17 layer/background separation)
     * composite over the right bg. 2026-05-16 以前に書かれた meta.json で
     * 取得した場合は `snapshotMetaSchema` の default (= bg-white) が入って
     * いる. (docs/_wip/layer-background-separation-2026-05-17.md)
     */
    background: { rgb: number; alpha: number };
    /**
     * conflict-aware Undo (C4) — the snapshot's `seq` (= its `lastSeq`),
     * forwarded into `engine.seedUndoBaseline` alongside `applySnapshot` so
     * the B' baseline reflects already-baked pixels instead of starting
     * blank (docs/_wip/undo-conflict-aware-2026-07-12.md「クライアント側の
     * 復元コスト」). `undefined` for the delta-only / no-snapshot path
     * (`layers: []`) — nothing to seed, watermark correctly stays 0.
     */
    snapshotSeq?: number;
    /**
     * `true` のとき apply 成功直後に「既存の絵を読み込みました」system-line を
     * 1 回 dispatch する. 「読み込んでいます…」を出した path (= welcome /
     * resume の snapshot mode) でのみ true にし、delta-only / engine-未登録の
     * バッファリングだけの path では false にする (= 何も告知してないのに
     * 「読み込みました」だけ出るのを避ける).
     */
    announceOnApply: boolean;
    /**
     * `true` のとき `applySnapshot` に `{ resetAll: true }` を渡す — 種 snapshot を
     * blit する前に全レイヤを wipe + in-flight stroke を掃除する (過去キャンバス
     * ライブ復元 hotfix, 2026-07-12)。復元経路だけ true。welcome / resume は
     * blank / 全差し替えなので false (省略) のまま既存挙動を維持。
     */
    resetAll?: boolean;
    /** 診断 (Sentry) 用の経路名。省略時は 'welcome' 扱い。 */
    source?: JoinReplaySource;
  } | null>(null);

  // Background-colour subscribers (A-4 ■ swatch seam). React (`useRoomChrome`)
  // owns the authoritative bg-colour state because the engine exposes no
  // getter; this ref-based emitter pushes it the remote sources of truth
  // (broadcast + snapshot meta) after the engine has been mutated. A plain Set
  // on a ref keeps `notifyBackground` stable (no re-subscribe churn) and never
  // re-renders the Provider.
  const backgroundListenersRef = useRef<Set<(rgb: number) => void>>(new Set());
  const notifyBackground = useCallback((rgb: number): void => {
    for (const cb of backgroundListenersRef.current) cb(rgb);
  }, []);
  const onBackgroundChange = useCallback<SessionContextValue['onBackgroundChange']>((cb) => {
    backgroundListenersRef.current.add(cb);
    return () => {
      backgroundListenersRef.current.delete(cb);
    };
  }, []);

  // Effect: when both engine + pendingSnapshot are present, apply.
  // Triggered both by snapshot fetch completion (via dispatch flush) and
  // by engine registration. `dispatch` is intentionally invoked just to
  // bump the render so this effect re-runs.
  const trySnapshotApply = useCallback((): void => {
    const engine = engineRef.current;
    const pending = pendingSnapshotRef.current;
    if (engine === null || pending === null) return;
    let applyOk = false;
    // background-set entries carried in the catch-up delta buffer (bug
    // 2026-06-20). The helper applies the last-wins bg via setBackground and
    // returns its rgb so the ■ swatch can prefer it over the snapshot's
    // (older) meta.background.
    let deltaBgRgb: number | null = null;
    const probe = createReplayProbe(pending.source ?? 'welcome', pending.deltaEvents.length);
    try {
      probe.run('applySnapshot', () => {
        engine.setBackground(pending.background);
        engine.applySnapshot(
          pending.layers.map((l) => ({ id: l.id, rgba: l.rgba })),
          // 復元経路のみ全レイヤ wipe を種 blit と atomic に行う (2026-07-12 hotfix)。
          pending.resetAll === true ? { resetAll: true } : undefined,
        );
      });
      // conflict-aware Undo (C4) — seed the B' baseline from the SAME layers
      // just blitted, so a later rewind falls back to real pixels instead of
      // blank. No-op (nothing to seed) when this apply had no snapshot.
      // resetAll のときは snapshot が無くても Undo baseline / 自分のストローク履歴を捨てる。
      if (pending.snapshotSeq !== undefined || pending.resetAll === true) {
        probe.run('applySnapshot', () =>
          engine.seedUndoBaseline(
            pending.layers.map((l) => ({ id: l.id, rgba: l.rgba })),
            pending.snapshotSeq ?? 0,
            pending.resetAll === true,
          ),
        );
      }
      if (pending.deltaEvents.length > 0) {
        deltaBgRgb = probe.run('replay', () =>
          replayDeltaEventsToEngine(engine, pending.deltaEvents),
        );
        probe.run('ingestUndo', () => engine.ingestUndoBroadcastBatch(pending.deltaEvents));
      }
      probe.finish();
      applyOk = true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[SessionProvider] applySnapshot/replay failed', err);
    }
    const announce = pending.announceOnApply;
    // Prefer the delta-derived bg over the snapshot's meta.background: a
    // background-set buffered after the snapshot generation is strictly newer,
    // so the ■ swatch should show it (and the engine already applied it).
    const swatchBgRgb = deltaBgRgb ?? pending.background.rgb;
    pendingSnapshotRef.current = null;
    setHydrating(false);
    if (applyOk) {
      // A-4 ■ swatch: seed React's bg-colour state from the (delta-or-snapshot)
      // background so the swatch shows the room's current colour on (re)join
      // (the engine was just set above but exposes no getter).
      notifyBackground(swatchBgRgb);
    }
    if (applyOk && announce) {
      dispatch({ type: 'system-line', text: '既存の絵を読み込みました' });
    }
  }, [notifyBackground]);

  // ─── helpers (defined before the transport listener so closure captures) ──

  const cancelReconnect = useCallback((): void => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  /**
   * 再接続中の fresh `room.join` の引数を組む (resume token が無い場合 / resume が
   * 拒否された場合の rejoin 共通). referrer は内部 reconnect なので乗せない
   * (二重計上防止)、clientBuild / layout / device は telemetry として毎回送る.
   */
  const buildRejoinOpts = useCallback(
    (ctx: { roomId: string; displayName: string; isRom: boolean }) => {
      // Phase 7.5: admin_token があれば再投入 (visitor 落ちで kick ボタンが消える事故を防ぐ).
      const adminToken = adminTokenStore.get(ctx.roomId);
      // Phase 8 Task 8-5: clientAnonId は同一ユーザ識別なので乗せる.
      const clientAnonId = anonUidStore.getOrCreate();
      // pen-usage-analytics-2026-08-18.md §9/§11.2/§11.3: layout / device は
      // `resolveJoinTelemetry` を通した **表示中の値** (同一 viewport スナップショット).
      const { layout, device } = resolveJoinTelemetry(
        readPhoneViewportInput(),
        layoutPreferenceStore.get(),
      );
      const entryPassword = lastJoinArgsRef.current?.entryPassword;
      return {
        roomId: ctx.roomId,
        displayName: ctx.displayName,
        isRom: ctx.isRom,
        ...(adminToken !== null && adminToken.length > 0 ? { adminToken } : {}),
        ...(clientAnonId !== null ? { clientAnonId } : {}),
        clientBuild: getClientBuild(),
        layout,
        device,
        ...dprField(),
        ...dotByDotField(),
        ...(entryPassword !== undefined && entryPassword.length > 0 ? { entryPassword } : {}),
      };
    },
    [adminTokenStore, anonUidStore, layoutPreferenceStore],
  );

  /**
   * Schedule the next reconnect attempt with the configured backoff,
   * then perform the actual lookup + WS connect + room.resume send.
   *
   * The retry loop is expressed as `setTimeout(...)` recursing back into
   * `scheduleReconnect()` — keeping it inside one `useCallback` avoids
   * Biome's `useExhaustiveDependencies` warning for cross-callback
   * references and keeps the closure capture story trivial (the only
   * captured values are stable singletons + refs).
   *
   * ws-heartbeat-2026-09-29.md §2-3: bails out (no timer, no attempt
   * consumed) while `document.hidden` — see `hiddenPendingReconnectRef`'s
   * doc comment. This guard also covers the recursive self-calls below
   * ('unavailable' / 'error' lookup outcomes, and a second `closed` status
   * mid-attempt), so a tab that gets backgrounded partway through a retry
   * chain stops consuming attempts from that point on too.
   *
   * `opts.force` skips the hidden gate for exactly one call — used by
   * `resumeDeferredReconnect`'s `online` path, which should get a fresh
   * attempt even if the tab hasn't come to the foreground yet (network
   * recovery is worth trying regardless of visibility; if that attempt
   * fails again the un-forced recursive retries re-apply the hidden gate
   * as normal, so a still-backgrounded tab doesn't loop).
   */
  const scheduleReconnect = useCallback(
    (opts?: { force?: boolean }): void => {
      if (opts?.force !== true && typeof document !== 'undefined' && document.hidden) {
        hiddenPendingReconnectRef.current = true;
        return;
      }
      // 次の試行が既に予約済みなら二重に積まない (2026-09-30 staging 実測)。
      // 試行中の WS が閉じると、その試行の onStatus listener と session 全体の
      // onStatus listener (phase 'reconnecting') の**両方**が同じ `closed` で
      // scheduleReconnect を呼ぶ。ガード無しだと 1 回の失敗で試行カウンタが 2 進み、
      // タイマーも 2 本立って以後の失敗ごとに鎖が倍々に増える (60 秒の停止で
      // 「17/19」まで進んで諦めていた)。1 本目が予約したタイマーがあれば 2 本目は捨てる。
      if (reconnectTimerRef.current !== null) return;
      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      if (attempt > MAX_RECONNECT_ATTEMPTS) {
        // Surface as a synthetic 'rejected' so the App routes to ErrorScreen.
        dispatch({
          type: 'server-event',
          event: {
            t: 'room.rejected',
            reason: 'internal-error',
            message: `自動再接続に ${MAX_RECONNECT_ATTEMPTS} 回失敗しました。`,
          },
        });
        sessionContextRef.current = null;
        return;
      }
      const delayMs = RECONNECT_BACKOFFS_MS[attempt - 1] ?? 15_000;
      const nextAttemptAt = Date.now() + delayMs;
      dispatch({ type: 'mark-reconnecting', attempt, nextAttemptAt });
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        void (async () => {
          const ctx = sessionContextRef.current;
          if (ctx === null) return; // cancelled (leave / fatal reject)
          // Re-resolve the dispatcher: the originally-assigned server may
          // have been migrated. Mirrors ReJoinScreen's lookup outcome
          // handling.
          const lookup = await dispatcherClient.lookupRoomServer(ctx.roomId);
          if (sessionContextRef.current === null) return; // cancelled meanwhile
          let wsUrl: string;
          switch (lookup.outcome) {
            case 'ok':
              wsUrl = lookup.wsUrl;
              break;
            case 'not-found':
              dispatch({
                type: 'server-event',
                event: { t: 'room.rejected', reason: 'room-not-found' },
              });
              sessionContextRef.current = null;
              return;
            case 'unavailable':
            case 'error':
              // Transient — retry per backoff.
              scheduleReconnect();
              return;
            case 'maintenance':
              // Phase 7 Task 7-6: dispatcher が「メンテナンス中」を返した.
              // 自動再接続を続けても 503 を踏み続けるだけなので、`rejected` で
              // ErrorScreen に倒して reload 待ちに切り替える. ユーザは
              // `waitUntil` 経過後に手動 reload する流れ.
              dispatch({
                type: 'server-event',
                event: {
                  t: 'room.rejected',
                  reason: 'maintenance',
                  message:
                    lookup.waitUntil === null
                      ? 'サーバはメンテナンス中です。再開時刻は未定です。'
                      : `サーバはメンテナンス中です。${new Date(lookup.waitUntil).toLocaleString('ja-JP')} まで待機してください。`,
                },
              });
              sessionContextRef.current = null;
              return;
          }
          sessionContextRef.current = { ...ctx, wsUrl };

          const stored = resumeTokenStore.get({ roomId: ctx.roomId, uid: ctx.uid });
          if (stored === null) {
            // No (or expired) token — fall back to a fresh join.
            // Loses seq history but keeps the user in the room.
            const joinOpts = buildRejoinOpts(ctx);
            freshRejoinRef.current = true;
            tx.connect(wsUrl);
            const off = tx.onStatus((status) => {
              if (status.t === 'open') {
                off();
                joinRoomCmd(tx, joinOpts);
              } else if (status.t === 'closed') {
                off();
                scheduleReconnect();
              }
            });
            return;
          }

          tx.connect(wsUrl);
          const off = tx.onStatus((status) => {
            if (status.t === 'open') {
              off();
              resumeInFlightRef.current = true;
              tx.send({
                t: 'room.resume',
                roomId: ctx.roomId,
                uid: ctx.uid,
                resumeToken: stored.token,
                lastSeq: lastSeqRef.current,
              });
            } else if (status.t === 'closed') {
              off();
              scheduleReconnect();
            }
          });
        })();
      }, delayMs);
    },
    [buildRejoinOpts, dispatcherClient, resumeTokenStore, tx],
  );

  /**
   * ws-heartbeat-2026-09-29.md §2-3: resume a reconnect that `scheduleReconnect`
   * deferred while `document.hidden`. Resets the attempt counter (the hidden
   * period doesn't count against the 5-try budget) and re-enters
   * `scheduleReconnect` from scratch. No-op when:
   *   - nothing was actually deferred (`hiddenPendingReconnectRef` false),
   *   - the session died meanwhile (explicit `leave()` / fatal reject nulls
   *     `sessionContextRef`), or
   *   - we're already connected (`tx.status.t === 'open'` — e.g. a stray
   *     `online` firing after the tab came back and already reconnected).
   */
  const resumeDeferredReconnect = useCallback((): void => {
    if (!hiddenPendingReconnectRef.current) return;
    if (sessionContextRef.current === null) return;
    if (tx.status.t === 'open') {
      hiddenPendingReconnectRef.current = false;
      return;
    }
    hiddenPendingReconnectRef.current = false;
    // Defensive: `scheduleReconnect` never leaves a timer running while
    // hidden (it bails before `setTimeout`), so this should be a no-op —
    // but guard against double-starting a retry chain regardless.
    cancelReconnect();
    reconnectAttemptRef.current = 0;
    // `force: true` — the `online` trigger may fire while still hidden (the
    // `visibilitychange` trigger only calls this once already-visible, so
    // the force is a no-op there).
    scheduleReconnect({ force: true });
  }, [cancelReconnect, scheduleReconnect, tx]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handleVisibilityChange = (): void => {
      if (document.hidden) return;
      resumeDeferredReconnect();
    };
    const handleOnline = (): void => {
      resumeDeferredReconnect();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [resumeDeferredReconnect]);

  // pagehide: 復帰せず閉じられる場合に備え、捨てた描画イベントの診断を 1 件報告する。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPageHide = (): void => {
      flushDroppedSends(droppedSendTrackerRef.current, 'pagehide', captureWarning);
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  // ─── Wire transport events / status ──────────────────────────────────────
  useEffect(() => {
    const offEvent = tx.onEvent((event) => {
      // Pre-dispatch: track lastSeq so room.resume can quote it.
      if ('seq' in event && typeof (event as { seq: unknown }).seq === 'number') {
        const seq = (event as { seq: number }).seq;
        if (seq > lastSeqRef.current) lastSeqRef.current = seq;
      }

      // 接続復帰 (welcome / resume-ok) で、捨てた描画イベントがあれば診断報告 (1 件)。
      if (event.t === 'room.welcome' || event.t === 'room.resume-ok') {
        flushDroppedSends(droppedSendTrackerRef.current, event.t, captureWarning);
      }

      // Welcome: persist resume token + record session context + queue
      // snapshot apply.
      if (event.t === 'room.welcome') {
        const w = event as RoomWelcome;
        lastSeqRef.current = w.lastSeq;
        resumeInFlightRef.current = false;
        // 再接続中の fresh join への welcome か (mount 済み engine へ流し込むので
        // resetAll で適用する)。1 回消費。
        const rebuild = freshRejoinRef.current;
        freshRejoinRef.current = false;
        if (rebuild) setHydrating(true);
        // snapshot 無し / 取得失敗でも、rebuild 時は空 snapshot + resetAll で旧絵を捨てて
        // から delta を replay する (applySnapshot([]) は resetAll 無しだと no-op のため)。
        const resetFromDelta = (): void => {
          pendingSnapshotRef.current = {
            layers: [],
            deltaEvents: w.deltaEvents,
            background: DEFAULT_SNAPSHOT_BACKGROUND,
            announceOnApply: false,
            resetAll: true,
            source: 'welcome',
          };
          trySnapshotApply();
        };
        // Record session context for the future reconnect path. wsUrl is
        // the one currently open on `tx`.
        const status = tx.status;
        const wsUrl = status.t === 'open' || status.t === 'connecting' ? status.url : '';
        sessionContextRef.current = {
          roomId: w.room.id,
          uid: w.uid,
          displayName: w.displayName,
          isRom: w.isRom,
          wsUrl,
        };
        // Persist resume token (TTL is the client's optimistic guess; the
        // server is the source of truth and will reject 'resume-expired'
        // if its own ledger has rotated).
        resumeTokenStore.save({
          roomId: w.room.id,
          uid: w.uid,
          token: w.resumeToken,
          expiresAt: Date.now() + RESUME_TOKEN_TTL_MS,
          permissions: w.permissions,
        });
        // 入れた = 成功. この表示名を端末に覚える (次回の入室画面の初期値).
        // rejected (あいことば違い・満員・BAN 等) は welcome が来ないので保存されない.
        displayNameStore.save(w.displayName);
        reconnectAttemptRef.current = 0;
        intentionalCloseRef.current = false;
        hiddenPendingReconnectRef.current = false;
        // 自分の入室 system-line. server-side broadcast は originator を除外
        // するため (Broadcaster / JoinService)、`room.member-joined` は他人
        // にしか届かない. self は welcome 受信時点で自前で 1 行積む
        // (= 旧版でも自分の入室は表示されていた挙動を踏襲).
        dispatch({ type: 'system-line', text: `${w.displayName} が入室しました` });
        // custom-cursor-2026-09-19.md §2.3: 保存済みカーソルがあれば、fresh join
        // 直後に一度だけ `member.cursor-change` を送って再確立する。resume 経路
        // (`room.resume-ok`) では server が既に持っているので送らない (=
        // 「resume 復帰時は server が持っているので送らない」、welcome だけの
        // 分岐に留める)。localStorage 保存を再度書き戻す必要はない (= 読んだ値を
        // そのまま送るだけ)。
        const savedCursor = customCursorStore.get();
        if (savedCursor !== null) {
          changeCursorCmd(tx, savedCursor);
        }
        // Kick off snapshot fetch (if any) — fire-and-forget.
        const welcomeSnapshot = w.snapshot;
        if (welcomeSnapshot === null && rebuild) {
          resetFromDelta();
        } else if (welcomeSnapshot !== null) {
          dispatch({ type: 'system-line', text: '途中入室 — 既存の絵を読み込んでいます…' });
          void (async () => {
            const out = await snapshotLoader(
              welcomeSnapshot.metaUrl,
              welcomeSnapshot.layerUrls,
              'welcome',
            );
            if (out.outcome !== 'ok') {
              // eslint-disable-next-line no-console
              console.warn('[SessionProvider] snapshot load failed', out);
              reportSnapshotLoadFailure(out, 'welcome');
              // 2026-06-05: 旧来は生の cause コード (例: `invalid`) をそのまま
              // チャット欄に出していたため、ユーザーが「壊れている」と誤解して
              // 不安になる問題があった. ここでの失敗の多くは本人に手の打ちようの
              // ない良性ケース (= incremental snapshot 仕様変更前の古いルームで、
              // 最新世代に無いレイヤーが過去世代を参照して 404 する cross-gen
              // ケース等) で、しかも後続 deltaEvents で部分復旧される. なので
              // 技術コードは console.warn に残しつつ、表示は穏やかな一文に留める.
              // `aborted` (= 新しい読み込みに差し替えられた / 離脱) は正常な中断
              // なので何も表示しない.
              if (out.cause !== 'aborted') {
                dispatch({
                  type: 'system-line',
                  text: '一部の既存の絵は読み込めませんでしたが、このまま参加できます',
                });
              }
              // Still apply deltaEvents so partial recovery happens (the
              // engine still has the background-painted canvas). bg-set in the
              // buffer is routed through setBackground by the helper; if any,
              // reflect it on the ■ swatch too (bug 2026-06-20).
              if (rebuild) {
                resetFromDelta();
              } else if (engineRef.current !== null && w.deltaEvents.length > 0) {
                const eng = engineRef.current;
                const probe = createReplayProbe('welcome', w.deltaEvents.length);
                const bg = probe.run('replay', () => replayDeltaEventsToEngine(eng, w.deltaEvents));
                if (bg !== null) notifyBackground(bg);
                probe.run('ingestUndo', () => eng.ingestUndoBroadcastBatch(w.deltaEvents));
                probe.finish();
              }
              return;
            }
            // 2026-06-05 per-layer degradation: 一部レイヤーが落ちても採用分は
            // 描く (outcome=ok). 落ちた id は診断ログにだけ残す (= ユーザーには
            // 出さない. 多くは古いルームの cross-gen 404 で本人に手の打ちようが
            // ない & 次の snapshot 再生成で自己修復するため).
            if (out.droppedLayerIds !== undefined && out.droppedLayerIds.length > 0) {
              // eslint-disable-next-line no-console
              console.warn('[SessionProvider] snapshot loaded with dropped layers', {
                droppedLayerIds: out.droppedLayerIds,
              });
            }
            pendingSnapshotRef.current = {
              layers: out.layers,
              deltaEvents: w.deltaEvents,
              background: out.meta.background,
              // conflict-aware Undo (C4) — seed the B' baseline from this
              // snapshot's `seq` once `trySnapshotApply` blits the pixels.
              snapshotSeq: welcomeSnapshot.seq,
              source: 'welcome',
              announceOnApply: true,
              ...(rebuild ? { resetAll: true } : {}),
            };
            trySnapshotApply();
          })().catch((err: unknown) => {
            // 2026-08-13 (Sentry WEB-5): fire-and-forget に catch が無く、
            // `loadSnapshot` が throw すると unhandled rejection になっていた。
            // ユーザーには「読み込んでいます…」の 1 行が出たまま**何も起きない**
            // (絵も出ず、下の穏やかな案内も出ない)。loadSnapshot 側も直したが、
            // 呼び出し側でも必ず着地させる (= 二重の防御)。
            // eslint-disable-next-line no-console
            console.warn('[SessionProvider] snapshot load threw', err);
            reportSnapshotLoadThrew(err, 'welcome');
            dispatch({
              type: 'system-line',
              text: '一部の既存の絵は読み込めませんでしたが、このまま参加できます',
            });
            if (rebuild) resetFromDelta();
          });
        } else if (w.deltaEvents.length > 0) {
          // No snapshot but there are events accumulated since session
          // start (rare — happens when the room is brand new and has only
          // had a few strokes that haven't yet hit the snapshot trigger).
          if (engineRef.current !== null) {
            // delta-only late-join: a bg-set in the buffer must reach both the
            // engine (via the helper's setBackground) and the ■ swatch (bug
            // 2026-06-20), otherwise the swatch and canvas disagree.
            const eng = engineRef.current;
            const probe = createReplayProbe('welcome', w.deltaEvents.length);
            const bg = probe.run('replay', () => replayDeltaEventsToEngine(eng, w.deltaEvents));
            if (bg !== null) notifyBackground(bg);
            probe.run('ingestUndo', () => eng.ingestUndoBroadcastBatch(w.deltaEvents));
            probe.finish();
          } else {
            // Engine not yet registered — stash so registerEngine flushes.
            // No snapshot to restore from; the engine keeps whatever bg it was
            // constructed with (= MVP default opaque white) UNLESS the buffered
            // deltaEvents carry a bg-set, which `trySnapshotApply` will route
            // through the helper on flush (and notifyBackground the ■ swatch).
            pendingSnapshotRef.current = {
              layers: [],
              deltaEvents: w.deltaEvents,
              background: DEFAULT_SNAPSHOT_BACKGROUND,
              announceOnApply: false,
            };
          }
        }
      }

      // Resume-ok: replay events / apply snapshot, refresh members /
      // permissions / lastSeq via the reducer.
      if (event.t === 'room.resume-ok') {
        resumeInFlightRef.current = false;
        // Update lastSeq from the envelope first.
        if (event.lastSeq > lastSeqRef.current) lastSeqRef.current = event.lastSeq;
        // Persist refreshed (uid, permissions) into the stored token.
        const ctx = sessionContextRef.current;
        if (ctx !== null) {
          const stored = resumeTokenStore.get({ roomId: ctx.roomId, uid: ctx.uid });
          if (stored !== null && stored.permissions !== event.permissions) {
            resumeTokenStore.save({
              roomId: ctx.roomId,
              uid: ctx.uid,
              token: stored.token,
              expiresAt: stored.expiresAt,
              permissions: event.permissions,
            });
          }
        }
        if (event.mode === 'delta') {
          if (engineRef.current !== null && event.events.length > 0) {
            // resume delta replay: route bg-set through setBackground and sync
            // the ■ swatch (bug 2026-06-20).
            const eng = engineRef.current;
            const probe = createReplayProbe('resume', event.events.length);
            const bg = probe.run('replay', () => replayDeltaEventsToEngine(eng, event.events));
            if (bg !== null) notifyBackground(bg);
            // conflict-aware Undo (C4) — `deltaSince` is NOT pair-filtered
            // (protocol-events.md §1.1 「deltaSince は生のまま」), so a
            // reconnecting client's resume delta CAN legitimately contain a
            // `stroke.cancelled` tombstone alongside the original stroke's
            // start/segment/end. Feed the whole batch so the store (and any
            // resulting rewind) stays consistent with what's replayed above.
            probe.run('ingestUndo', () => eng.ingestUndoBroadcastBatch(event.events));
            probe.finish();
          } else if (event.events.length > 0) {
            pendingSnapshotRef.current = {
              layers: [],
              deltaEvents: event.events,
              background: DEFAULT_SNAPSHOT_BACKGROUND,
              announceOnApply: false,
            };
          }
        } else {
          // mode === 'snapshot'
          dispatch({ type: 'system-line', text: '再接続中 — 既存の絵を読み込んでいます…' });
          // joined に戻っても適用が終わるまで描画ロックを保つ (適用前の線が消えないように)。
          setHydrating(true);
          void (async () => {
            const out = await snapshotLoader(
              event.snapshot.metaUrl,
              event.snapshot.layerUrls,
              'resume',
            );
            if (out.outcome !== 'ok') {
              // eslint-disable-next-line no-console
              console.warn('[SessionProvider] resume snapshot load failed', out);
              reportSnapshotLoadFailure(out, 'resume');
              // 2026-06-05: welcome 側と同方針 — 生 cause を出さず穏やかな一文に.
              // 後続 deltaEvents で部分復旧する. `aborted` は何も表示しない.
              if (out.cause !== 'aborted') {
                dispatch({
                  type: 'system-line',
                  text: '再接続時に一部の絵を読み込めませんでしたが、このまま続けられます',
                });
              }
              if (engineRef.current !== null && event.deltaEvents.length > 0) {
                // Partial recovery: bg-set in the buffer goes through
                // setBackground; reflect on the ■ swatch too (bug 2026-06-20).
                const eng = engineRef.current;
                const probe = createReplayProbe('resume', event.deltaEvents.length);
                const bg = probe.run('replay', () =>
                  replayDeltaEventsToEngine(eng, event.deltaEvents),
                );
                if (bg !== null) notifyBackground(bg);
                probe.run('ingestUndo', () => eng.ingestUndoBroadcastBatch(event.deltaEvents));
                probe.finish();
              }
              setHydrating(false);
              return;
            }
            pendingSnapshotRef.current = {
              layers: out.layers,
              deltaEvents: event.deltaEvents,
              background: out.meta.background,
              // conflict-aware Undo (C4) — seed the B' baseline once
              // `trySnapshotApply` blits these pixels.
              snapshotSeq: event.snapshot.seq,
              source: 'resume',
              announceOnApply: true,
            };
            trySnapshotApply();
          })().catch((err: unknown) => {
            // welcome 側と同じ理由 (Sentry WEB-5)。throw を必ず着地させる。
            // eslint-disable-next-line no-console
            console.warn('[SessionProvider] resume snapshot load threw', err);
            reportSnapshotLoadThrew(err, 'resume');
            setHydrating(false);
            dispatch({
              type: 'system-line',
              text: '再接続時に一部の絵を読み込めませんでしたが、このまま続けられます',
            });
          });
        }
        reconnectAttemptRef.current = 0;
      }

      // 過去キャンバス復元 broadcast (Slice 3, past-canvas-new-document-
      // 2026-07-05.md §4.1): 全レイヤを新セッションの種 snapshot (welcome の
      // `snapshotRef` と同形の presigned 参照) で bit-perfect に差し替える。
      // welcome / resume-ok と同じ hydration 経路 (snapshotLoader →
      // pendingSnapshotRef → trySnapshotApply) を broadcast 起点で呼ぶ。
      // system 行 (「○○ が過去のキャンバスを復元しました」) と lastSeq 追従は
      // reducer 側 (`applyServerEvent` の `whiteboard.restore` case) が担う。
      if (event.t === 'whiteboard.restore') {
        const restore = event;
        void (async () => {
          const out = await snapshotLoader(
            restore.snapshot.metaUrl,
            restore.snapshot.layerUrls,
            'restore',
          );
          if (out.outcome !== 'ok') {
            // eslint-disable-next-line no-console
            console.warn('[SessionProvider] restore snapshot load failed', out);
            reportSnapshotLoadFailure(out, 'restore');
            // welcome / resume 側と同方針 — 生 cause を出さず穏やかな一文に.
            // 復元後の deltaEvents は無い (server は broadcast seq S まで
            // deleteUntil 済) ので部分復旧も無し。再入室 (= welcome snapshot)
            // で自己修復する。`aborted` は正常な中断なので何も表示しない.
            if (out.cause !== 'aborted') {
              dispatch({
                type: 'system-line',
                text: '復元された絵を読み込めませんでした。入室し直すと表示されます',
              });
            }
            return;
          }
          if (out.droppedLayerIds !== undefined && out.droppedLayerIds.length > 0) {
            // eslint-disable-next-line no-console
            console.warn('[SessionProvider] restore snapshot loaded with dropped layers', {
              droppedLayerIds: out.droppedLayerIds,
            });
          }
          // clear broadcast 受信時と同等の後始末を、種 snapshot 適用と atomic に行う。
          //
          // 【ピクセル系】全レイヤ wipe + in-flight remote stroke の掃除 は
          // `applySnapshot({ resetAll: true })` に内包した (下の pendingSnapshot)。
          // 以前はここで `applyRemoteStroke([clear])` を呼んでいたが、それは
          // remote-coalesce queue 経由の **遅延** postMessage で worker に届く一方、
          // `applySnapshot` は即時 `send` で届くため、worker 経路では seed が先に
          // 適用され → 次フレームで遅延 clear が全レイヤを白く wipe してキャンバスが
          // 真っ白になった (2026-07-12 hotfix)。resetAll は wipe を seed blit と
          // 同一メッセージ内で atomic に行うので順序逆転が構造的に起きない。
          //
          // 【非ピクセル系】name label は main-thread 状態なので worker メッセージ
          // 順序と無関係。ここで即時に掃除する (clear が本来担っていた後始末)。
          // engine 未登録 (canvas mount 前) のときは fresh engine が blank なので
          // 何もしなくてよい — pendingSnapshot は registerEngine 時に flush される。
          if (engineRef.current !== null) {
            engineRef.current.clearNameLabels();
          }
          pendingSnapshotRef.current = {
            layers: out.layers,
            deltaEvents: [],
            background: out.meta.background,
            // 復元の告知は reducer の system 行が既に出しているので、apply 時の
            // 「既存の絵を読み込みました」は重ねない。
            announceOnApply: false,
            // 復元種に無いレイヤ (旧セッションで描かれた余剰レイヤ) のピクセルが
            // 生き残らないよう、全レイヤ wipe を seed blit と atomic に行う。
            resetAll: true,
            // conflict-aware Undo (C4) — fresh B' baseline seeded from the
            // restored snapshot's `seq` (resetAll=true above discards every
            // previously-tracked stroke, matching the pixel wipe).
            snapshotSeq: restore.snapshot.seq,
            source: 'restore',
          };
          trySnapshotApply();
        })();
      }

      // Rejected with resume failures: drop the stored token + force a
      // fresh entry. Phase 6 will rework UX; here we route through the
      // reducer so App's existing 'rejected' branch handles it.
      if (event.t === 'room.rejected') {
        // wss 再起動後の自動 rejoin: resume への応答として部屋 / token が無いと
        // 拒否された場合 (メモリのみの状態が飛んだ) は、部屋は DB に残っているので
        // ErrorScreen に落とさず fresh join し直す. `resumeInFlightRef` を消費して
        // から動くので 1 回だけ (rejoin の join への拒否は従来経路).
        const wasResuming = resumeInFlightRef.current;
        resumeInFlightRef.current = false;
        const rejoinCtx = sessionContextRef.current;
        if (
          wasResuming &&
          rejoinCtx !== null &&
          (event.reason === 'room-not-found' ||
            event.reason === 'resume-expired' ||
            event.reason === 'invalid-resume')
        ) {
          resumeTokenStore.clear({ roomId: rejoinCtx.roomId, uid: rejoinCtx.uid });
          // close handler の scheduleReconnect を止める (旧ソケットの close 中).
          sessionContextRef.current = null;
          cancelReconnect();
          // phase は reconnecting のまま保つ (`mark-joining` で state を捨てると
          // RoomLayout が unmount → welcome で再 mount してお知らせ再ポップ / engine 再生成
          // になる)。welcome は既存 RoomLayout に流し込み、resetAll で絵を差し替える。
          freshRejoinRef.current = true;
          // invalid-entry-password → ReJoinScreen 誘導が読むので、join() を経ていない
          // (= 引数が無い) 場合でも session 情報から補っておく.
          lastJoinArgsRef.current ??= {
            roomId: rejoinCtx.roomId,
            displayName: rejoinCtx.displayName,
            isRom: rejoinCtx.isRom,
            wsUrl: rejoinCtx.wsUrl,
          };
          const joinOpts = buildRejoinOpts(rejoinCtx);
          const connectAndJoin = (): void => {
            // 新 ctx 相当を戻す: connect 自体が失敗して close しても通常の
            // 再接続 (token 無し → fresh join) に乗る. uid は welcome で更新される.
            sessionContextRef.current = rejoinCtx;
            tx.connect(rejoinCtx.wsUrl);
            const offOpen = tx.onStatus((s2) => {
              if (s2.t === 'open') {
                offOpen();
                joinRoomCmd(tx, joinOpts);
              } else if (s2.t === 'closed') {
                offOpen();
              }
            });
          };
          if (tx.status.t === 'closed' || tx.status.t === 'idle') {
            connectAndJoin();
          } else {
            // server は rejected 直後に close する. close を観測してから connect.
            const offClose = tx.onStatus((s1) => {
              if (s1.t !== 'closed') return;
              offClose();
              connectAndJoin();
            });
          }
          return;
        }
        if (event.reason === 'resume-expired' || event.reason === 'invalid-resume') {
          const ctx = sessionContextRef.current;
          if (ctx !== null) {
            resumeTokenStore.clear({ roomId: ctx.roomId, uid: ctx.uid });
          }
          sessionContextRef.current = null;
          cancelReconnect();
        }
        // Phase 6 Task 7 確定方針 AG: `'invalid-credential'` / `'rate-limited'`
        // も reconnect 抑止. 認証失敗 / レート制限は時間を置いても (= backoff
        // で再試行しても) 改善しないので、自動再接続を打ち切って ErrorScreen
        // に誘導する. App.tsx の renderRejected が UI を出す.
        // ('kicked' / 'banned' / 'room-not-found' / 'maintenance' は既存の
        //  member.left or close-handler の流れ + reducer への dispatch で
        //  reconnect 抑止される — `intentionalCloseRef` に倒れるか、最終的に
        //  `phase = 'rejected'` で close handler の defensive guard が発火.)
        // 公式部屋の BAN (`banned`) も同様: 再接続しても同じ判定で弾かれるだけなので
        // 明示的に打ち切る (reducer の phase 反映前に close が来る race でも
        // close handler が scheduleReconnect しないように).
        if (
          event.reason === 'invalid-credential' ||
          event.reason === 'rate-limited' ||
          event.reason === 'banned'
        ) {
          intentionalCloseRef.current = true;
          sessionContextRef.current = null;
          cancelReconnect();
        }
        // 入室あいことば不一致 (owner-vs-entry-password-2026-06-13.md 実装
        // スライス): `invalid-credential` と違い ErrorScreen には誘導しない —
        // 入室画面 (ReJoinScreen) に戻してあいことば欄の下でエラーを再表示し、
        // 再入力→再入室させる (「あいことば」自体はオーナーパスワードと違って
        // 再試行できる軽い鍵、という UX 判断).
        //
        // 流れ (server は rejected 直後に WS を close する前提、
        // invalid-admin-token と同じ前提):
        //   a. reconnect 抑止 (invalid-credential と同じ形 — sessionContextRef
        //      を null にし、intentionalCloseRef を立てる).
        //   b. `entryPasswordRejection` state に roomId / displayName を積む
        //      — `mark-idle` で ReJoinScreen が remount した後でも Context 越し
        //      に読める (React state なので remount を跨いで生存する).
        //   c. reducer の phase を `idle` に巻き戻す。`rejected` のまま流すと
        //      RootSwitch が ErrorScreen に倒れてしまうため、URL 駆動の
        //      ReJoinScreen 分岐に戻す (= 'joining' のままだと「入室中…」
        //      スピナーで止まってしまうので使えない).
        // reducer には `rejected` を流さない (ErrorScreen に誘導しないため) —
        // 関数全体を early return する (invalid-admin-token と同じ流儀).
        if (event.reason === 'invalid-entry-password') {
          const lastArgs = lastJoinArgsRef.current;
          intentionalCloseRef.current = true;
          sessionContextRef.current = null;
          cancelReconnect();
          if (lastArgs !== null) {
            setEntryPasswordRejection({
              roomId: lastArgs.roomId,
              displayName: lastArgs.displayName,
            });
          }
          dispatch({ type: 'mark-idle' });
          return;
        }
        // Phase 7.5: admin_token verify NG (DB 不在 or 別ルームの token).
        // ErrorScreen に誘導するのではなく、admin_token を破棄して visitor
        // で自動 fallback する (= 一般メンバーとして入室を継続).
        //
        // 流れ (server は rejected を投げた直後に WS を close する前提):
        //   a. admin_token を localStorage から消す (orphan / 別ルーム token
        //      の再利用防止).
        //   b. sessionContextRef を null にして close handler 側の
        //      scheduleReconnect 経路を抑止 (close handler は
        //      `sessionContextRef === null` で early return する).
        //   c. system 行で UI 通知.
        //   d. reducer の phase を `joining` に戻す.
        //   e. server からの close を待ってから fresh `tx.connect()` →
        //      visitor `room.join` の順で再投入する.
        //
        // 呼び出し時点 (= rejected 受信時) ではまだソケットが open のため
        // `tx.connect()` は no-op. 必ず close を観測してから connect する.
        if (event.reason === 'invalid-admin-token') {
          const lastArgs = lastJoinArgsRef.current;
          if (lastArgs !== null) {
            // (a) admin_token を捨てる.
            adminTokenStore.clear(lastArgs.roomId);
            // (b) reconnect 経路を切る. intentional flag は false のまま
            //     (= 自動再接続抑止は sessionContextRef=null 経由で実現する).
            //     resume token も使い回さない (= visitor で fresh join).
            sessionContextRef.current = null;
            cancelReconnect();
            // (c) UI 通知.
            dispatch({
              type: 'system-line',
              text: 'オーナー権限が無効化されました。一般メンバーとして入室します',
            });
            // (d) reducer 上は joining に戻す (rejected は流さない).
            dispatch({
              type: 'mark-joining',
              roomId: lastArgs.roomId,
              displayName: lastArgs.displayName,
            });
            // (e) close → connect → open → visitor join をチェーン.
            //     close handler 側でも `sessionContextRef === null` で
            //     scheduleReconnect しないので、ここで一意に visitor join を
            //     issue する形になる.
            const offClose = tx.onStatus((status) => {
              if (status.t !== 'closed') return;
              offClose();
              tx.connect(lastArgs.wsUrl);
              const offOpen = tx.onStatus((s2) => {
                if (s2.t === 'open') {
                  offOpen();
                  joinRoomCmd(tx, {
                    roomId: lastArgs.roomId,
                    displayName: lastArgs.displayName,
                    isRom: lastArgs.isRom,
                    // adminToken は意図的に渡さない (= visitor mode).
                  });
                } else if (s2.t === 'closed') {
                  offOpen();
                  // visitor connect 段階で落ちた場合は普通の reconnect 経路
                  // に戻す術がない (sessionContextRef は null のまま) ので、
                  // ユーザに reload を促すしかない. UI には system 行が
                  // 残っているので、再 reload で fresh ReJoinScreen に乗る.
                }
              });
            });
            // reducer には rejected を流さない (= phase='rejected' で
            // ErrorScreen に誘導しない). 関数全体を early return.
            return;
          }
        }
      }

      // Member-left for our own uid: kick treatment. Stop the auto-
      // reconnect dance — coming back would just be re-kicked. The reducer
      // also transitions phase → 'kicked' on `reason === 'kick'` so the
      // App routes to KickedScreen (Phase 7 Task 7-4).
      if (event.t === 'room.member-left') {
        const selfUid = selfUidRef.current;
        if (selfUid !== null && event.uid === selfUid) {
          intentionalCloseRef.current = true;
          sessionContextRef.current = null;
          cancelReconnect();
        }
      }

      // Future-only chat filter (ユーザ確認 2026-05-06): フィルタ ON 中に
      // 来た `chat.message` は reducer に流さず捨てる (drawing engine の
      // setStrokeFilter と同方針). フィルタ解除しても state.chat に残らない
      // ため復活しない. self の発言は `shouldDispatchChatMessage` 内で skip.
      // 他の event (room.* / member.* / stroke.* / system.*) はそのまま流す.
      if (
        event.t === 'chat.message' &&
        !shouldDispatchChatMessage(event, selfUidRef.current, filteredUidsRef.current)
      ) {
        return;
      }

      dispatch({ type: 'server-event', event });
      // Replay drawing broadcasts onto the local engine. Echoes of our
      // own strokes are filtered inside `routeBroadcastToEngine`. The
      // `whiteboard.background-set` broadcast is also applied here (direct
      // `engine.setBackground`, not via `applyRemoteStroke`) — see the
      // router's own doc comment for why bg-set is exempt from echo
      // suppression (mirrors whiteboard.clear: broadcast = source of truth).
      routeBroadcastToEngine(event, selfUidRef.current, engineRef.current);
      // A-4 ■ swatch seam: after the engine has applied the bg-set broadcast,
      // push the new colour to React (`useRoomChrome`) so the ■ updates — for
      // EVERY client including non-owners and the originator's own echo
      // (idempotent; the engine/router already de-dupe the apply).
      if (event.t === 'whiteboard.background-set') {
        notifyBackground(event.rgb);
      }
      // conflict-aware Undo (C4) — feed EVERY event (self AND remote) into
      // the B' baseline mechanism, deliberately NOT gated by the self-uid
      // echo suppression `routeBroadcastToEngine` applies above (the B'
      // baseline needs full stroke geometry regardless of author). No-op for
      // event kinds it doesn't recognise (chat / member / room / system /…).
      engineRef.current?.ingestUndoBroadcast(event);
      // `stroke.cancel-rejected` is a unicast response. It already flowed
      // through `dispatch({ type: 'server-event', event })` above (falls to
      // the reducer's `default` — no reason→system-line mapping yet, unlike
      // `admin.kick-rejected`; adding one is a later UI task, mirrors how
      // `whiteboard.imagepaste-rejected` is also reducer-silent today).
      // Additionally forward it to the engine's own listener registry so a
      // future UI can react without threading more state through the
      // reducer (see `sendCancelStroke` doc comment).
      if (event.t === 'stroke.cancel-rejected' || event.t === 'whiteboard.region-cancel-rejected') {
        engineRef.current?.notifyCancelRejected(event);
      }
    });

    const offStatus = tx.onStatus((status) => {
      if (status.t !== 'closed') return;
      // Decide whether to schedule a reconnect.
      // - intentional close (leave) → bail
      // - no session context (never welcomed, or session voided by
      //   resume-expired / kick / fatal reject) → bail
      // - normal close code 1000 from server (rare) → bail
      if (intentionalCloseRef.current) return;
      if (sessionContextRef.current === null) return;
      if (status.code === 1000) return;
      // Defensive: if the reducer is not in a 'joined'/'reconnecting'
      // state, there's nothing UX-meaningful to keep alive. Phase 7 Task
      // 7-4: 'kicked' falls into the same bucket — re-attempting would
      // just get re-kicked, so we never schedule a reconnect from it.
      const phaseTag = phaseTagRef.current;
      if (phaseTag !== 'joined' && phaseTag !== 'reconnecting' && phaseTag !== 'joining') {
        return;
      }
      scheduleReconnect();
    });

    const offError = tx.onError((err) => {
      if (err.t === 'send-before-open') {
        droppedSendTrackerRef.current.record(err.eventType, {
          now: Date.now(),
          phase: phaseTagRef.current,
          visibility: typeof document === 'undefined' ? 'unknown' : document.visibilityState,
          reconnecting:
            phaseTagRef.current === 'reconnecting' || reconnectTimerRef.current !== null,
        });
      }
      // eslint-disable-next-line no-console
      console.warn('[SessionProvider] transport error', err);
    });
    return () => {
      offEvent();
      offStatus();
      offError();
    };
    // The deps below are all stable across the page lifetime: `tx` and
    // the stores live on `useRef`s, the reconnect / snapshot helpers
    // are `useCallback`s with stable inputs, and `phaseTagRef` /
    // `selfUidRef` carry the per-render mutable state. Listing them
    // keeps the linter happy without re-firing the subscription.
  }, [
    adminTokenStore,
    buildRejoinOpts,
    cancelReconnect,
    customCursorStore,
    displayNameStore,
    notifyBackground,
    resumeTokenStore,
    scheduleReconnect,
    snapshotLoader,
    trySnapshotApply,
    tx,
  ]);

  // ─── public surface ──────────────────────────────────────────────────────
  // Phase 7 Task 7-10b: keep a ref of the latest filteredUids so registerEngine
  // can re-flush it on engine remount without depending on `filteredUids`
  // (which would re-create the callback every toggle and re-subscribe
  // DrawingCanvas's effect).
  const filteredUidsRef = useRef<ReadonlySet<string>>(filteredUids);
  filteredUidsRef.current = filteredUids;
  const registerEngine = useCallback<SessionContextValue['registerEngine']>(
    (engine) => {
      engineRef.current = engine;
      // If welcome / resume-ok already landed, the snapshot is waiting on
      // an engine — flush now.
      if (engine !== null) {
        // Phase 7 Task 7-10b: re-apply the current filter set to the new
        // engine instance. Without this, a canvas remount would silently
        // lose the filter (engine starts with an empty filterUids).
        engine.setStrokeFilter(filteredUidsRef.current);
        trySnapshotApply();
      }
    },
    [trySnapshotApply],
  );

  const join = useCallback<SessionContextValue['join']>(
    (args) => {
      // Fresh join clears the reconnect bookkeeping.
      cancelReconnect();
      reconnectAttemptRef.current = 0;
      intentionalCloseRef.current = false;
      hiddenPendingReconnectRef.current = false;
      // Phase 7.5: 直近 join 引数を保存. `invalid-admin-token` fallback / resume
      // 失敗時の visitor fallback で displayName / isRom / wsUrl を再利用する.
      lastJoinArgsRef.current = {
        roomId: args.roomId,
        displayName: args.displayName,
        isRom: args.isRom ?? false,
        wsUrl: args.wsUrl,
        ...(args.entryPassword !== undefined && args.entryPassword.length > 0
          ? { entryPassword: args.entryPassword }
          : {}),
      };
      resumeInFlightRef.current = false;
      freshRejoinRef.current = false;
      // Phase 7.5: localStorage に admin_token があれば自動で `mode: 'admin'` を
      // 投入 (`useCases.joinRoom` 内で adminToken の有無で auth 形を切り替える).
      // 無ければ従来通り `mode: 'visitor'`. credential 経路は本 helper 経由
      // ではなく上位 (LoginPage 等) で別組立てする想定なので、ここでは admin /
      // visitor の 2 系統だけを扱う.
      const adminToken = adminTokenStore.get(args.roomId);
      // Phase 8 Task 8-5: anonUid を localStorage から取得 (初回 lazy 発行).
      // `null` (Storage 不可 / crypto.randomUUID 不在) のときは payload に
      // 載せない — server 側で session 限定 ID にフォールバックする.
      const clientAnonId = anonUidStore.getOrCreate();
      // Phase 8 Task 8-5: マウント時に snapshot した `?ref=...` を載せる.
      // referrer は内部 reconnect では送らず、ユーザの入口 (= `join` 経路) で
      // のみ送る (= 二重計上防止).
      const referrer = referrerRef.current;
      // A5 (analytics-operations-2026-08-13.md §2 表 A5): every room.join —
      // including this fresh-entry path — carries clientBuild.
      const clientBuild = getClientBuild();
      // pen-usage-analytics-2026-08-18.md §9/§11.2/§11.3: 入室時点で client が
      // 表示している **表示中の** レイアウト + シェル/pointer を乗せる。
      // clientBuild と同じ流儀 (常に読んで送る) — 「入口ファネル計測」では
      // なく「どちらを表示中か」の telemetry なので referrer のような一度きり
      // 制限は不要. `layout` は `layoutPreferenceStore.get()` の生値 (= 保存
      // された好み) をそのまま送らない — `resolveJoinTelemetry` でスマホ強制
      // Modern 解決を経由させないと、PC 細窓 / iPad Split View 等で
      // 「好みは classic だが実際は Modern フォンシェル」のとき
      // `device='phone'` かつ `layout='classic'` の矛盾行が出る
      // (2026-09-27 fable-advisor レビュー反映). `layout` / `device` は同一の
      // viewport スナップショット (`readPhoneViewportInput()` を入室時に 1 回
      // だけ読む) から出す. matchMedia が無い環境 (SSR / テスト) は
      // readPhoneViewportInput のフォールバック (fine pointer 扱い) を経由して
      // device='desktop' / layout=stored に倒れる.
      const { layout, device } = resolveJoinTelemetry(
        readPhoneViewportInput(),
        layoutPreferenceStore.get(),
      );
      const joinOpts = {
        roomId: args.roomId,
        displayName: args.displayName,
        ...(args.isRom !== undefined ? { isRom: args.isRom } : {}),
        ...(adminToken !== null && adminToken.length > 0 ? { adminToken } : {}),
        ...(clientAnonId !== null ? { clientAnonId } : {}),
        ...(referrer !== null ? { referrer } : {}),
        clientBuild,
        layout,
        device,
        ...dprField(),
        ...dotByDotField(),
        // 入室あいことば (owner-vs-entry-password-2026-06-13.md): admin_token
        // 保有時は打たない仕様なので、adminToken が既に決まっている場合でも
        // 呼び出し側 (`useCases.joinRoom`) の visitor 分岐でしか使われない
        // (admin 分岐では黙って無視される) — ここでは単に args を素通しする.
        ...(args.entryPassword !== undefined && args.entryPassword.length > 0
          ? { entryPassword: args.entryPassword }
          : {}),
      };
      // `connect` is a no-op if already open. We chain join() through a
      // status listener so the join goes out *after* the socket is open.
      if (tx.status.t !== 'open') {
        tx.connect(args.wsUrl);
        const off = tx.onStatus((status) => {
          if (status.t === 'open') {
            off();
            joinRoomCmd(tx, joinOpts);
          } else if (status.t === 'closed') {
            off();
          }
        });
      } else {
        joinRoomCmd(tx, joinOpts);
      }
      dispatch({ type: 'mark-joining', roomId: args.roomId, displayName: args.displayName });
    },
    [adminTokenStore, anonUidStore, cancelReconnect, layoutPreferenceStore, tx],
  );

  const sendChat = useCallback<SessionContextValue['sendChat']>(
    (text) => {
      if (connectionLockedRef.current) return;
      sendChatCmd(tx, text);
    },
    [tx],
  );

  const setRom = useCallback<SessionContextValue['setRom']>(
    (rom) => {
      if (connectionLockedRef.current) return;
      setRomCmd(tx, rom);
    },
    [tx],
  );

  const sendNameChange = useCallback<SessionContextValue['sendNameChange']>(
    (displayName) => changeNameCmd(tx, displayName),
    [tx],
  );

  const sendCursorChange = useCallback<SessionContextValue['sendCursorChange']>(
    (cursor) => {
      changeCursorCmd(tx, cursor);
      if (cursor === null) {
        customCursorStore.clear();
      } else {
        customCursorStore.save(cursor);
      }
    },
    [tx, customCursorStore],
  );

  const sendCursorDraft = useCallback<SessionContextValue['sendCursorDraft']>(
    (cursor) => draftCursorCmd(tx, cursor),
    [tx],
  );

  const sendKick = useCallback<SessionContextValue['sendKick']>(
    (args) => sendKickCmd(tx, args),
    [tx],
  );

  const sendClear = useCallback<SessionContextValue['sendClear']>(
    (layerId) => {
      if (connectionLockedRef.current) return;
      sendClearCmd(tx, layerId);
    },
    [tx],
  );

  const sendCancelStroke = useCallback<SessionContextValue['sendCancelStroke']>(
    (sid) => {
      if (connectionLockedRef.current) return;
      sendCancelStrokeCmd(tx, sid);
    },
    [tx],
  );

  const sendCancelRegion = useCallback<SessionContextValue['sendCancelRegion']>(
    (regionSeq) => {
      if (connectionLockedRef.current) return;
      sendCancelRegionCmd(tx, regionSeq);
    },
    [tx],
  );

  const sendBackgroundSet = useCallback<SessionContextValue['sendBackgroundSet']>(
    (rgb) => {
      if (connectionLockedRef.current) return;
      sendBackgroundSetCmd(tx, rgb);
    },
    [tx],
  );

  const sendImagepasteRequest = useCallback<SessionContextValue['sendImagepasteRequest']>(
    (args) => sendImagepasteRequestCmd(tx, args),
    [tx],
  );
  const sendImagepasteConfirm = useCallback<SessionContextValue['sendImagepasteConfirm']>(
    (uploadId) => sendImagepasteConfirmCmd(tx, uploadId),
    [tx],
  );

  const pushSystemLine = useCallback<SessionContextValue['pushSystemLine']>((text) => {
    dispatch({ type: 'system-line', text });
  }, []);

  const clearChatLog = useCallback<SessionContextValue['clearChatLog']>(() => {
    dispatch({ type: 'clear-chat-log' });
  }, []);

  const leave = useCallback<SessionContextValue['leave']>(() => {
    intentionalCloseRef.current = true;
    hiddenPendingReconnectRef.current = false;
    freshRejoinRef.current = false;
    setHydrating(false);
    cancelReconnect();
    const ctx = sessionContextRef.current;
    if (ctx !== null) {
      resumeTokenStore.clear({ roomId: ctx.roomId, uid: ctx.uid });
    }
    sessionContextRef.current = null;
    // Phase 8 Task 8-5: 明示退室で referrer をリセット.  以後の in-app 再入室
    // (= 入口イベントではない) で同じ referrer を二重計上しないため.
    // anonUid は **clear しない** — タブを閉じずに再入室するときも同一ユーザ
    // として retention 集計したいので、永続化したまま.
    referrerRef.current = null;
    leaveRoomCmd(tx);
    dispatch({ type: 'mark-left' });
  }, [cancelReconnect, resumeTokenStore, tx]);

  // Cleanup on unmount.
  useEffect(() => cancelReconnect, [cancelReconnect]);

  const value = useMemo<SessionContextValue>(
    () => ({
      view,
      transport: tx,
      dispatcher: dispatcherClient,
      adminTokens: adminTokenStore,
      resumeTokens: resumeTokenStore,
      displayNames: displayNameStore,
      join,
      entryPasswordRejection,
      clearEntryPasswordRejection,
      sendChat,
      setRom,
      sendNameChange,
      sendCursorChange,
      sendCursorDraft,
      sendKick,
      sendClear,
      sendCancelStroke,
      sendCancelRegion,
      sendBackgroundSet,
      onBackgroundChange,
      sendImagepasteRequest,
      sendImagepasteConfirm,
      leave,
      registerEngine,
      pushSystemLine,
      clearChatLog,
      filteredUids,
      toggleFilter,
    }),
    [
      view,
      tx,
      dispatcherClient,
      adminTokenStore,
      resumeTokenStore,
      displayNameStore,
      join,
      entryPasswordRejection,
      clearEntryPasswordRejection,
      sendChat,
      setRom,
      sendNameChange,
      sendCursorChange,
      sendCursorDraft,
      sendKick,
      sendClear,
      sendCancelStroke,
      sendCancelRegion,
      sendBackgroundSet,
      onBackgroundChange,
      sendImagepasteRequest,
      sendImagepasteConfirm,
      leave,
      registerEngine,
      pushSystemLine,
      clearChatLog,
      filteredUids,
      toggleFilter,
    ],
  );

  return (
    <ConnectionLockContext value={connectionLock}>
      <SessionContext value={value}>{children}</SessionContext>
    </ConnectionLockContext>
  );
};
